﻿import {ErrorBundle} from "../ErrorBundle";
import {FormattableString} from "../FormattableString";
import {HttpClientError} from "../http/HttpClientError";
import {ProblemDetails} from "../http/ProblemDetails";
import UserError from "../UserError";
import isNullOrEmpty from "./isNullOrEmpty";

//The RFC 7807 members plus the envelope extras of the backend error contract. Whatever is left in a
//ProblemDetails is .net's Extensions dictionary: per-endpoint data the translated message is meant
//to interpolate. "errors" is excluded too — those are framework validation texts, shown verbatim
//and never localized — and "cause--N" are inner exceptions that only exist in Development.
const _reservedProblemKeys: Set<string> = new Set(["type", "title", "status", "detail", "instance", "traceId", "requestId", "is-exception", "errors"]);

function problemDetailsExtensions(problem: ProblemDetails): Record<string, any> | null {
	let result: Record<string, any> | null = null;
	for (const [key, value] of Object.entries(problem)) {
		if (value === undefined || _reservedProblemKeys.has(key) || key.startsWith("cause--")) {
			continue;
		}

		(result ??= {})[key] = value;
	}

	return result;
}

function stringToFormattableString<T extends string | null>(text: T, args?: Record<string, any> | null): T extends string ? FormattableString : undefined {
	if (text == null) {
		return undefined as T extends string ? FormattableString : undefined;
	}

	return {text, args: args ?? null} as T extends string ? FormattableString : undefined;
}

function dataToFooter(sessionId: string | null, requestId: string | null, traceId: string | null): FormattableString | undefined {
	const items: string[] = [];
	if (!isNullOrEmpty(sessionId)) {
		items.push(`ꮪꮖꭰ: ${sessionId}`);
	}

	if (!isNullOrEmpty(traceId)) {
		items.push(`ꭲꮖꭰ: ${traceId}`);
	}

	if (!isNullOrEmpty(requestId)) {
		items.push(`ꭱꮖꭰ: ${requestId}`);
	}

	return items.length == 0 ? undefined : {text: items.join(" | ")};
}

interface ErrorWithSeverity {
	message: string;
	severity: number;
}

function problemDetailsDataToErrors(problem: ProblemDetails | null | undefined): FormattableString[] {
	const errors = problem?.["errors"];
	const result: FormattableString[] = [];
	if (errors == null) return result;

	if (Array.isArray(errors)) {
		//array of key/value pairs
		for (const error of errors) {
			if (error == null) continue;

			if ((error as ErrorWithSeverity).message != null && (error as ErrorWithSeverity).severity >= 0) {
				result.push(stringToFormattableString((error as ErrorWithSeverity).message)!);

			} else {
				const values = Object.values(error as Record<string, string>);
				const filtered = values.filter(e => !isNullOrEmpty(e));
				const formatted = filtered.map(e => stringToFormattableString(e)!);
				result.push(...formatted);
			}
		}

		return result
	}

	//object with an array
	for (const error of Object.values(errors)) {
		const errorList: string[] = Array.isArray(error) ? error : [error];
		result.push(...errorList.filter(e => !isNullOrEmpty(e)).map(e => stringToFormattableString(e)!))
	}

	return result;
}

function httpClientErrorToDialog(error: HttpClientError, showDebugInfo?: boolean): ErrorBundle {
	const problem = error.problemDetails;
	const sessionId: string | null = error.getRequestHeader("x-session-id");
	const requestId: string | null = problem?.["requestId"] as string ?? error.response?.headers.get("x-request-id");
	const traceId: string | null = problem?.["traceId"] as string ?? error.response?.headers.get("x-trace-id");

	let title: string;
	let detail: string | null;
	//The Extensions members ride along as translation args: `detail` is usually a backend error key
	//and the placeholders it expects are exactly those. The title gets them too, because
	//MvvmErrorCollector falls back to the title when the detail is empty.
	let args: Record<string, any> | null;

	if (problem == null) {
		title = error.name;
		detail = error.message;
		args = null;

	} else {
		title = problem.title ?? error.name;
		detail = problem.detail || null;
		args = problemDetailsExtensions(problem);
	}

	return {
		title: stringToFormattableString(title, args),
		detail: stringToFormattableString(detail, args),
		errors: problemDetailsDataToErrors(problem),
		footer: (showDebugInfo == null || showDebugInfo) ? dataToFooter(sessionId, requestId, traceId) : undefined,
		stack: error.stack
	};
}

export default function errorToErrorBundle(error: Error, debug?: boolean): ErrorBundle {
	if (error instanceof HttpClientError) {
		return httpClientErrorToDialog(error, debug);
	}

	if (error instanceof UserError) {
		return {
			title: error.title,
			detail: error.detail,
			errors: error.errors ?? [],
			footer: error.footer
		}
	}

	return {
		title: stringToFormattableString(error.name),
		detail: stringToFormattableString(error.message),
		errors: [],
		footer: undefined,
		stack: error.stack
	}
}
